import {Fragment, useEffect, useLayoutEffect, useRef, useState} from "react";

import {Badge} from "@/components/ui/badge";
import {anchoredPopoverMaxHeightClass} from "@/components/ui/anchored-positioner-defaults";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {cn} from "@/lib/utils";
import {
  BADGE_OVERFLOW_GAP_PX,
  computeBadgeOverflowFit,
} from "@/src/modules/core/lib/compute-badge-overflow-fit";
import {suspendScrollContainerLock} from "@/src/modules/core/lib/scroll-container-lock-suspend";

type Align = "start" | "center" | "end" | "inline-start" | "inline-end";

type BaseBadgeOverflowListProps<T> = {
  items: readonly T[];
  maxVisible?: number;
  getKey: (item: T, index: number) => string;
  popoverTitle: string;
  empty?: React.ReactNode;
  className?: string;
  popoverContentClassName?: string;
  visibleBadgeVariant?: React.ComponentProps<typeof Badge>["variant"];
  visibleBadgeClassName?: string;
  /** Merged with visible styles for chips inside the overflow popover (e.g. drop `truncate`, allow wrap). */
  popoverItemClassName?: string;
  moreTriggerClassName?: string;
  moreLabel?: (overflowCount: number) => React.ReactNode;
  listClassName?: string;
  titleClassName?: string;
  align?: Align;
  moreTriggerAriaLabel?: string;
  /** Rich layout for items inside the overflow popover only; visible chips still use `getLabel`. */
  renderPopoverItem?: (item: T, index: number) => React.ReactNode;
  /**
   * When true, the first visible label is ellipsis-clamped so badges cannot spill into adjacent table cells.
   * Set false when `visibleBadgeClassName` uses wrapping (e.g. multi-line citation type chips).
   */
  clampVisibleLabel?: boolean;
  /**
   * `grid` (default): compact pair for datatable cells. `flex`: visible chip + “+more” wrap without a
   * minmax(0,1fr) column that crushes long labels (e.g. patrol vehicle names on shift cards).
   */
  overflowPairLayout?: "grid" | "flex";
  /** When true, measures the container and shows as many badges as fit before “+N more”. */
  fitToWidth?: boolean;
};

type WithLabel<T> = BaseBadgeOverflowListProps<T> & {
  getLabel: (item: T) => React.ReactNode;
  renderItem?: undefined;
};

type WithRenderItem<T> = BaseBadgeOverflowListProps<T> & {
  renderItem: (item: T, index: number) => React.ReactNode;
  getLabel?: undefined;
};

export type BadgeOverflowListProps<T> = WithLabel<T> | WithRenderItem<T>;

export function BadgeOverflowList<T>({
  items,
  maxVisible = 1,
  getKey,
  getLabel,
  renderItem,
  renderPopoverItem,
  popoverTitle,
  empty = null,
  className,
  popoverContentClassName,
  visibleBadgeVariant = "secondary",
  visibleBadgeClassName,
  popoverItemClassName,
  moreTriggerClassName,
  moreLabel = (n) => `+${n} more`,
  listClassName,
  titleClassName,
  align = "center",
  moreTriggerAriaLabel,
  clampVisibleLabel = true,
  overflowPairLayout = "grid",
  fitToWidth = false,
}: BadgeOverflowListProps<T>) {
  const [overflowPopoverOpen, setOverflowPopoverOpen] = useState(false);
  const [richPopoverOpen, setRichPopoverOpen] = useState(false);
  const [fitMaxVisible, setFitMaxVisible] = useState(() => (fitToWidth ? 1 : items.length));
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  const itemLabelKey = fitToWidth
    ? items
        .map((item, index) =>
          renderItem ? getKey(item, index) : String(getLabel!(item)),
        )
        .join("\0")
    : "";

  useLayoutEffect(() => {
    if (!fitToWidth) return;

    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure || items.length === 0) return;

    const recompute = () => {
      const host = container.parentElement ?? container;
      const available = host.clientWidth;
      if (available <= 0) return;

      const measureChildren = Array.from(measure.children) as HTMLElement[];
      const itemWidths = measureChildren.slice(0, items.length).map((el) => el.offsetWidth);
      const moreBadgeWidth = measureChildren[items.length]?.offsetWidth ?? 0;

      setFitMaxVisible(
        computeBadgeOverflowFit(available, itemWidths, moreBadgeWidth, BADGE_OVERFLOW_GAP_PX),
      );
    };

    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(container.parentElement ?? container);
    return () => observer.disconnect();
  }, [fitToWidth, items.length, itemLabelKey, visibleBadgeClassName, visibleBadgeVariant]);

  useEffect(() => {
    if (!overflowPopoverOpen && !richPopoverOpen) return;
    return suspendScrollContainerLock();
  }, [overflowPopoverOpen, richPopoverOpen]);

  if (items.length === 0) {
    return <>{empty}</>;
  }

  const effectiveMaxVisible = fitToWidth ? fitMaxVisible : maxVisible;
  const visible = items.slice(0, effectiveMaxVisible);

  const chip = (item: T, index: number, context: "visible" | "popover") => {
    if (context === "popover" && renderPopoverItem) {
      return renderPopoverItem(item, index);
    }
    if (renderItem) {
      return renderItem(item, index);
    }
    const badgeCn =
      context === "popover" && popoverItemClassName
        ? cn(visibleBadgeClassName, popoverItemClassName)
        : cn(
            visibleBadgeClassName,
            context === "visible" && clampVisibleLabel && "min-w-0 max-w-full shrink",
          );
    return (
      <Badge variant={visibleBadgeVariant} className={badgeCn}>
        {context === "visible" && clampVisibleLabel ? (
          <span className="block min-w-0 truncate">{getLabel!(item)}</span>
        ) : (
          getLabel!(item)
        )}
      </Badge>
    );
  };

  const ariaLabel =
    moreTriggerAriaLabel ??
    `Show all ${items.length} — ${popoverTitle.replace(/^\s*all\s+/i, "").trim()}`;

  const overflowCount = items.length - visible.length;
  const singleItemRichPopover = items.length === 1 && Boolean(renderPopoverItem) && overflowCount === 0;

  const popoverPanel = (
    <div className="flex min-h-0 flex-col gap-2">
      <p className={cn("text-foreground shrink-0 text-sm font-medium", titleClassName)}>
        {popoverTitle}
      </p>
      <div
        className={cn(
          "min-h-0 flex-1 overflow-y-auto overscroll-contain",
          listClassName ?? "flex flex-wrap gap-2",
        )}
      >
        {items.map((item, index) => (
          <Fragment key={getKey(item, index)}>{chip(item, index, "popover")}</Fragment>
        ))}
      </div>
    </div>
  );

  const popoverContentClass = cn(
    "w-64 overflow-hidden",
    anchoredPopoverMaxHeightClass,
    popoverContentClassName,
  );

  const layoutClass = fitToWidth
    ? "flex w-full min-w-0 max-w-full flex-nowrap items-center gap-2 overflow-hidden"
    : overflowCount > 0 || singleItemRichPopover
      ? overflowPairLayout === "flex"
        ? "flex w-full min-w-0 flex-wrap items-center gap-2"
        : "grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-2"
      : visible.length === 1
        ? "grid w-full min-w-0 grid-cols-1 gap-2"
        : "flex min-w-0 w-full flex-wrap items-center gap-2";

  return (
    <div ref={containerRef} className={cn(layoutClass, className)}>
      {fitToWidth ? (
        <div
          ref={measureRef}
          aria-hidden
          className="pointer-events-none invisible absolute flex items-center gap-2"
        >
          {items.map((item, index) => (
            <Fragment key={`measure-${getKey(item, index)}`}>
              {chip(item, index, "visible")}
            </Fragment>
          ))}
          <Badge variant="outline" className={moreTriggerClassName}>
            +99 more
          </Badge>
        </div>
      ) : null}
      {visible.map((item, index) => {
        const visibleChip = chip(item, index, "visible");

        if (!singleItemRichPopover) {
          return <Fragment key={getKey(item, index)}>{visibleChip}</Fragment>;
        }

        return (
          <Popover
            key={getKey(item, index)}
            open={richPopoverOpen}
            onOpenChange={setRichPopoverOpen}
          >
            <PopoverTrigger
              nativeButton={false}
              render={
                <Badge
                  variant={visibleBadgeVariant}
                  className={cn("hover:bg-muted cursor-pointer font-normal", visibleBadgeClassName)}
                  aria-label={ariaLabel}
                />
              }
            >
              {clampVisibleLabel ? (
                <span className="block min-w-0 truncate">{getLabel!(item)}</span>
              ) : (
                getLabel!(item)
              )}
            </PopoverTrigger>
            <PopoverContent
              align={
                align === "inline-start" || align === "inline-end"
                  ? align === "inline-start"
                    ? "start"
                    : "end"
                  : align
              }
              className={popoverContentClass}
            >
              {popoverPanel}
            </PopoverContent>
          </Popover>
        );
      })}
      {overflowCount > 0 ? (
        <Popover open={overflowPopoverOpen} onOpenChange={setOverflowPopoverOpen}>
          <PopoverTrigger
            nativeButton={false}
            render={
              <Badge
                variant="outline"
                className={cn("hover:bg-muted cursor-pointer", moreTriggerClassName)}
                aria-label={ariaLabel}
              />
            }
          >
            {moreLabel(overflowCount)}
          </PopoverTrigger>
          <PopoverContent
            align={
              align === "inline-start" || align === "inline-end"
                ? align === "inline-start"
                  ? "start"
                  : "end"
                : align
            }
            className={popoverContentClass}
          >
            {popoverPanel}
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}
